// Finance → Settings: e-Fiscalization (Poreska uprava EFI) configuration + certificate check.
import React, { useEffect, useState } from 'react';
import { ShieldCheck, Loader2, Save, KeyRound } from 'lucide-react';
import { apiCall } from './_shared';
import TcrRegisterPanel from './TcrRegisterPanel';

const FIELDS = [
  ['tin', 'PIB (TIN)'], ['business_unit', 'Business unit code'], ['tcr', 'TCR / ENU code'], ['software', 'Software code'],
  ['maintainer', 'Maintainer code'], ['operator', 'Operator code'], ['seller_name', 'Seller name'], ['seller_address', 'Address'], ['seller_town', 'Town'],
];

const FiscalSettingsPanel = ({ canManage }) => {
  const [s, setS] = useState(null);
  const [pw, setPw] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');
  const [cert, setCert] = useState(null);
  const load = () => apiCall('/api/admin/fiscal/settings').then((d) => { setS(d); setCert(d.cert_info || null); }).catch((e) => setMsg(e.message));
  useEffect(() => { load(); }, []);
  if (!s) return <div className="bg-white rounded-xl border p-4 text-sm text-slate-500" data-testid="fiscal-settings">Loading fiscalization settings…</div>;

  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy('save'); setMsg('');
    try {
      const body = { env: s.env, tin: s.tin, business_unit: s.business_unit, tcr: s.tcr, software: s.software, maintainer: s.maintainer, operator: s.operator,
        seller_name: s.seller_name, seller_address: s.seller_address, seller_town: s.seller_town, is_issuer_in_vat: s.is_issuer_in_vat, vat_rate: Number(s.vat_rate),
        tz_offset: Number(s.tz_offset), auto_fiscalize: !!s.auto_fiscalize, ...(pw ? { p12_password: pw } : {}) };
      const d = await apiCall('/api/admin/fiscal/settings', { method: 'PUT', body: JSON.stringify(body) });
      setS((x) => ({ ...x, ...d })); setPw(''); setMsg('Saved');
    } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  };
  const check = async () => {
    setBusy('cert'); setMsg('');
    try { const c = await apiCall('/api/admin/fiscal/certificate/check', { method: 'POST' }); setCert(c); setMsg('Certificate OK'); }
    catch (e) { setMsg(e.message); } finally { setBusy(''); }
  };

  const inputs = FIELDS.map(([k, label]) => (
    <label key={k} className="text-xs text-slate-600">
      {label}
      <input value={s[k] || ''} onChange={(e) => set(k, e.target.value)} disabled={!canManage} className="mt-0.5 w-full border rounded px-2 py-1 text-sm font-mono" data-testid={`fiscal-input-${k}`} />
    </label>
  ));

  return (
    <div className="bg-white rounded-xl border p-4 space-y-3" data-testid="fiscal-settings">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-bold text-slate-900 inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> e-Fiscalization (Poreska uprava)</h3>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${s.ready ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'}`} data-testid="fiscal-ready">
          {s.ready ? 'Ready' : `Not ready: ${[...(s.missing || []), !s.has_certificate && 'certificate', !s.has_password && 'password'].filter(Boolean).join(', ')}`}
        </span>
        <span className="text-xs text-slate-500">{s.stats?.fiscalized ?? 0} fiscalized · {s.stats?.failed ?? 0} failed · {s.stats?.pending ?? 0} pending</span>
        <span className="flex-1" />
        <label className="text-xs inline-flex items-center gap-1">Environment
          <select value={s.env} onChange={(e) => set('env', e.target.value)} disabled={!canManage} className="border rounded px-1 py-0.5 font-semibold" data-testid="fiscal-env">
            <option value="test">TEST (efitest)</option><option value="prod">PRODUCTION (efi)</option>
          </select>
        </label>
      </div>
      <div className="grid sm:grid-cols-3 gap-2">{inputs}</div>
      <div className="flex flex-wrap items-end gap-3 text-xs text-slate-600">
        <label>VAT rate %<input type="number" step="0.5" value={s.vat_rate} onChange={(e) => set('vat_rate', e.target.value)} disabled={!canManage} className="mt-0.5 w-20 border rounded px-2 py-1 text-sm" data-testid="fiscal-vat" /></label>
        <label>Time zone offset<input type="number" value={s.tz_offset} onChange={(e) => set('tz_offset', e.target.value)} disabled={!canManage} className="mt-0.5 w-16 border rounded px-2 py-1 text-sm" data-testid="fiscal-tz" /></label>
        <label className="inline-flex items-center gap-1 pb-1.5"><input type="checkbox" checked={!!s.is_issuer_in_vat} onChange={(e) => set('is_issuer_in_vat', e.target.checked)} disabled={!canManage} /> Issuer in VAT system</label>
        <label className="flex-1 min-w-[220px]"><span className="inline-flex items-center gap-1"><KeyRound className="w-3 h-3" /> Certificate password {s.has_password && <span className="text-emerald-700">(set)</span>}</span>
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder={s.has_password ? '•••••••• (leave blank to keep)' : 'Enter .pfx password'} disabled={!canManage} className="mt-0.5 w-full border rounded px-2 py-1 text-sm" data-testid="fiscal-password" />
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {canManage && <button type="button" onClick={save} disabled={!!busy} className="inline-flex items-center gap-1 bg-slate-900 text-white rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-60" data-testid="fiscal-save">{busy === 'save' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button>}
        {canManage && <button type="button" onClick={check} disabled={!!busy} className="inline-flex items-center gap-1 border rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60" data-testid="fiscal-check-cert">{busy === 'cert' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Check certificate</button>}
        <span className="text-xs text-slate-500">Certificate file: {s.has_certificate ? 'HOXHA SAHIBE (Potpis_Autentifikacija).pfx uploaded' : 'missing'} · endpoint {s.endpoint}</span>
        {msg && <span className="text-sm font-semibold text-slate-800" data-testid="fiscal-msg">{msg}</span>}
      </div>
      <TcrRegisterPanel canManage={canManage} onRegistered={(code) => set('tcr', code)} />
      {cert && (
        <div className="text-xs bg-slate-50 rounded-lg p-2 font-mono" data-testid="fiscal-cert-info">
          {cert.subject} · valid {String(cert.valid_from).slice(0, 10)} → {String(cert.valid_to).slice(0, 10)}{cert.expired && <b className="text-red-600"> EXPIRED</b>}
        </div>
      )}
    </div>
  );
};

export default FiscalSettingsPanel;
